import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthProvider';
import { kindLabels, listEventMarks, type EventMark } from '../events/api';

/** A camera's events on the timeline: a bucket of one kind. */
export interface TimelineMark {
  at: number;
  last: number;
  tone: 'crit' | 'warn' | 'info';
  label: string;
}

/** Playback starts this long before an event, to show what led to it. */
export const preRollMs = 5_000;

export function toTimelineMarks(items: EventMark[]): TimelineMark[] {
  return items
    .map((m) => ({
      at: Date.parse(m.first_at),
      last: Date.parse(m.last_at),
      tone:
        m.severity === 'critical'
          ? ('crit' as const)
          : m.severity === 'warning'
            ? ('warn' as const)
            : ('info' as const),
      label: `${kindLabels[m.kind] ?? m.kind}${m.count > 1 ? ` ×${String(m.count)}` : ''}`,
    }))
    .filter((m) => Number.isFinite(m.at))
    .sort((a, b) => a.at - b.at);
}

/**
 * The day's event marks of a camera, grouped per minute by the server. Empty
 * without events.view: the timeline simply shows none.
 */
export function useEventMarks(deviceId: string, [from, to]: [number, number], live: boolean) {
  const { request, can } = useAuth();
  const marks = useQuery({
    queryKey: ['event-marks', deviceId, from, to],
    queryFn: () => listEventMarks(request, deviceId, from, to, 60),
    enabled: can('events.view'),
    retry: false,
    // Today keeps happening.
    refetchInterval: live ? 60_000 : false,
  });
  return marks.data ? toTimelineMarks(marks.data) : [];
}

/** The mark starting before t (by more than the pre-roll), or null. */
export function markBefore(marks: TimelineMark[], t: number): TimelineMark | null {
  let found: TimelineMark | null = null;
  for (const m of marks) {
    if (m.at < t - preRollMs - 1000) found = m;
  }
  return found;
}

/**
 * The first mark starting after t, or null. A mark within the pre-roll ahead
 * is the one just jumped to: the next is the one after it.
 */
export function markAfter(marks: TimelineMark[], t: number): TimelineMark | null {
  return marks.find((m) => m.at > t + preRollMs + 1000) ?? null;
}
